import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { BiPlus, BiEdit, BiTrash, BiCheck, BiX } from 'react-icons/bi'
import { agencyService, Agency, UserInvite } from '../services/agencyService'
import { useCurrentUser } from '../hooks/useCurrentUser'

import { AddAgencyModal } from '../components/AddAgencyModal'

const Agencies = () => {
  const navigate = useNavigate()
  const { user } = useCurrentUser()
  const [agencies, setAgencies] = useState<Agency[]>([])
  const [userInvites, setUserInvites] = useState<UserInvite[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingInvites, setIsLoadingInvites] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  })
  const [isAddAgencyModalOpen, setIsAddAgencyModalOpen] = useState(false)

  // Fetch agencies and invites from API
  useEffect(() => {
    fetchAgencies()
    fetchUserInvites()
  }, [currentPage])

  const fetchAgencies = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const { agencies: fetchedAgencies, pagination: paginationData } = await agencyService.getAllAgencies({
        page: currentPage,
        limit: 10
      })

      setAgencies(fetchedAgencies)
      setPagination(paginationData)
    } catch (err) {
      // Only log errors in development or when explicitly needed
      if (import.meta.env.DEV || import.meta.env.VITE_DEBUG === 'true') {
        console.error('Error fetching agencies:', err)
      }
      setError('Failed to load agencies. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchUserInvites = async () => {
    try {
      setIsLoadingInvites(true)
      const invites = await agencyService.getUserInvites()

      // Filter out non-pending invites as a safety measure
      // The backend should only return pending invites, but we'll double-check here
      const pendingInvites = invites.filter(invite => invite.inviteStatus === 'pending')

      setUserInvites(pendingInvites)
    } catch (err) {
      // Only log errors in development or when explicitly needed
      if (import.meta.env.DEV || import.meta.env.VITE_DEBUG === 'true') {
        console.error('Error fetching user invites:', err)
      }
      // Don't set error for invites as it's not critical
    } finally {
      setIsLoadingInvites(false)
    }
  }

  const handleAcceptInvite = async (invite: UserInvite) => {
    try {
      if (!user?.id) {
        throw new Error('User not authenticated')
      }

      await agencyService.acceptInvite(invite.agencyId, user.id)

      // Remove from the list
      setUserInvites(prev => prev.filter(inv => inv.agencyId !== invite.agencyId))

      // Refresh agencies list to show the newly accepted agency
      await fetchAgencies()
    } catch (err: any) {
      console.error('Error accepting invite:', err)
      alert(err?.message || 'Failed to accept invitation. Please try again.')
    }
  }

  const handleRejectInvite = async (invite: UserInvite) => {
    try {
      if (!user?.id) {
        throw new Error('User not authenticated')
      }

      await agencyService.rejectInvite(invite.agencyId, user.id)

      // Remove from the list
      setUserInvites(prev => prev.filter(inv => inv.agencyId !== invite.agencyId))
    } catch (err: any) {
      console.error('Error rejecting invite:', err)
      alert(err?.message || 'Failed to reject invitation. Please try again.')
    }
  }

  const handleCreateAgency = () => {
    // Check if user is a system admin (has admin role)
    const isSystemAdmin = user?.roles?.includes('admin') || false

    if (isSystemAdmin) {
      // Show modal for system admin
      setIsAddAgencyModalOpen(true)
    } else {
      // Navigate to wizard for regular users
      navigate('/agencies/create')
    }
  }

  const handleAgencyAdded = (agencyId?: string) => {
    if (agencyId) {
      // Redirect to the created agency page
      navigate(`/agencies/${agencyId}`)
    } else {
      // Fallback: refresh the agencies list
      fetchAgencies()
    }
  }

  const handleEditAgency = (e: React.MouseEvent, agencyId: string) => {
    e.stopPropagation()
    navigate(`/agencies/${agencyId}/edit`)
  }

  const handleDeleteAgency = async (e: React.MouseEvent, agencyId: string) => {
    e.stopPropagation()

    if (!confirm('Are you sure you want to delete this agency? This action cannot be undone.')) {
      return
    }

    try {
      await agencyService.deleteAgency(agencyId)
      await fetchAgencies()
      // Show success message (you could use a toast library here)
      alert('Agency deleted successfully')
    } catch (err: any) {
      // Only log errors in development or when explicitly needed
      if (import.meta.env.DEV || import.meta.env.VITE_DEBUG === 'true') {
        console.error('Error deleting agency:', err)
      }
      alert(err?.message || 'Failed to delete agency. Please try again.')
    }
  }



  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={fetchAgencies}
                  className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Agencies</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage your partner agencies and their information
          </p>
        </div>
        <button
          onClick={handleCreateAgency}
          className="flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          <BiPlus size={20} />
          <span>Create Agency</span>
        </button>
      </div>

      {/* User Invites Section */}
      {!isLoadingInvites && userInvites.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Pending Invitations ({userInvites.length})
          </h2>
          <div className="space-y-3">
            {userInvites.map((invite) => (
              <div
                key={invite.agencyId}
                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 flex items-center justify-between"
              >
                <div className="flex-1">
                  <h3 className="font-medium text-slate-900 dark:text-white">
                    {invite.agencyName}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Agency Code: {invite.agencyCode} • Role: {invite.role} • Payment: {invite.paymentResponsibility}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                    Invited: {formatDate(invite.invitedAt)}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleAcceptInvite(invite)}
                    className="flex items-center space-x-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors"
                  >
                    <BiCheck size={16} />
                    <span>Accept</span>
                  </button>
                  <button
                    onClick={() => handleRejectInvite(invite)}
                    className="flex items-center space-x-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors"
                  >
                    <BiX size={16} />
                    <span>Reject</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agencies Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {agencies.map((agency) => (
          <div
            key={agency.id}
            className={`bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6 transition-shadow ${agency.agents !== undefined
              ? 'hover:shadow-lg cursor-pointer'
              : 'cursor-default'
              }`}
            onClick={agency.agents !== undefined ? () => navigate(`/agencies/${agency.id}`) : undefined}
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                {agency.name}
              </h3>
            </div>

            <div className="space-y-2 text-sm">
              <p className="text-slate-600 dark:text-slate-400">
                <span className="font-medium">Agency Code:</span> {agency.agencyCode}
              </p>
            </div>

            {/* Show different info based on user role */}
            {agency.agents !== undefined ? (
              // Admin view - show agent count and billing status
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    {agency.agents.length} total agents
                  </span>
                  <div className="flex space-x-2">
                    {agency.billing?.subscriptions && agency.billing.subscriptions.length > 0 && agency.billing.subscriptions[0]?.stripeSubscriptionId ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // Agent view - show simple member badge
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    Member
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                    Agent
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="mt-8 flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-400">
            <span>
              Showing {((currentPage - 1) * pagination.limit) + 1} to {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} agencies
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-2 text-sm font-medium text-slate-500 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-800 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-700"
            >
              Previous
            </button>

            {/* Page numbers */}
            <div className="flex space-x-1">
              {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(currentPage - 2 + i, pagination.pages - 4 + i + 1))
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === pageNum
                      ? 'bg-primary-600 text-white'
                      : 'text-slate-500 bg-white border border-slate-300 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-700'
                      }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= pagination.pages}
              className="px-3 py-2 text-sm font-medium text-slate-500 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-800 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-700"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && agencies.length === 0 && (
        <div className="text-center py-12">
          <div className="text-slate-400 mb-4">
            <BiPlus size={48} className="mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
            No agencies yet
          </h3>
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            Get started by creating your first agency.
          </p>
          <button
            onClick={handleCreateAgency}
            className="inline-flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <BiPlus size={20} />
            <span>Create Agency</span>
          </button>
        </div>
      )}

      {/* Add Agency Modal */}
      <AddAgencyModal
        isOpen={isAddAgencyModalOpen}
        onClose={() => setIsAddAgencyModalOpen(false)}
        onAgencyAdded={handleAgencyAdded}
      />
    </div>
  )
}

export default Agencies 